<template>
  <div class="queue-screen" data-testid="queue-screen">
    <BzScreen :title="$t('message.queue')" :back-label="$t('message.back')" back-testid="queue-back" @back="goBack">
      <BzListGroup v-if="photos.length">
        <div v-for="photo in photos" :key="photo.key" class="queue-photo" data-testid="queue-photo">
          <img :src="photo.imageUrl" alt="" class="queue-photo__image"/>
          <span class="queue-photo__text">
            <span v-if="photo.orderDetails.deliveryId || photo.orderDetails.ticketId" class="queue-photo__code" translate="no">{{ photo.orderDetails.deliveryId || photo.orderDetails.ticketId }}</span>
            <span v-else class="queue-photo__code">{{ $t('message.unknownOrder') }}</span>
            <span class="queue-photo__time">{{ formatTimestamp(photo.timestamp) }}</span>
          </span>
        </div>
      </BzListGroup>
      <!-- Nothing waiting: said only once the store has answered, so a slow read never reads as «all uploaded». -->
      <div v-else-if="loaded" class="bz-empty" data-testid="queue-empty">
        <BzIcon name="check_circle" class="bz-empty__icon" />
        <span class="bz-empty__title">{{ $t('message.queueEmpty') }}</span>
        <span class="bz-empty__text">{{ $t('message.queueEmptyHint') }}</span>
      </div>
    </BzScreen>
  </div>
</template>

<script>
import Logger from '@/logger';
import BzIcon from '@/ui/components/BzIcon.vue';
import BzListGroup from '@/ui/components/BzListGroup.vue';
import BzScreen from '@/ui/components/BzScreen.vue';

const logger = new Logger({screen: 'QueueScreen'});

export default {
  name: 'QueueScreen',
  components: { BzIcon, BzListGroup, BzScreen },
  inject: ['db'],
  data() {
    return {
      photos: [],
      // True once the store has answered, empty or not.
      loaded: false,
    };
  },
  mounted() {
    this.loadPhotos();
  },
  beforeUnmount() {
    this.photos.forEach(photo => {
      if (photo.imageUrl) {
        URL.revokeObjectURL(photo.imageUrl);
      }
    });
  },
  methods: {
    async loadPhotos() {
      try {
        const dbInstance = this.db();
        const photos = await this.getAllFromStore(dbInstance, 'photos');
        const orders = await this.getAllFromStore(dbInstance, 'orders');

        this.photos = photos.map(photo => ({
          ...photo,
          imageUrl: URL.createObjectURL(photo.image),
          orderDetails: orders.find(order => order.photos.includes(photo.key)) || {}
        }));
        this.loaded = true;
      } catch (error) {
        logger.error('Error loading photos', {error: error});
      }
    },
    getAllFromStore(dbInstance, storeName) {
      return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = (event) => resolve(event.target.result);
        request.onerror = (event) => reject(event.target.error);

        // Ensure the transaction completes
        transaction.oncomplete = () => logger.console(`${storeName} transaction completed`);
        transaction.onerror = (event) => logger.console(`${storeName} transaction error: ${event.target.error}`);
      });
    },
    goBack() {
      this.$emit('go-back');
    },
    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
  },
  props: {
    restaurant: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
};
</script>

<style scoped>
/* The queue is on the design system (docs/sistema-diseno/README.md): its screen and its group are the
   library's, and what is here is only a photo's row. */
.queue-screen {
  position: absolute;
  inset: 0;
}

/* A waiting photo: its thumbnail, the order's code and when it was taken. The hairline between two
   rows starts where the text does, as BzListRow draws it. */
.queue-photo {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--bz-space-4);
  box-sizing: border-box;
  min-height: var(--bz-target-primary);
  padding: var(--bz-space-3) var(--bz-space-4);
}

.queue-photo + .queue-photo::before {
  content: '';
  position: absolute;
  top: 0;
  left: calc(var(--bz-space-4) + var(--bz-target-md) + var(--bz-space-4));
  right: 0;
  height: 1px;
  background: var(--bz-edge);
  pointer-events: none;
}

.queue-photo__image {
  flex: 0 0 auto;
  width: var(--bz-target-md);
  height: var(--bz-target-md);
  border-radius: var(--bz-radius-md);
  object-fit: cover;
  background: var(--bz-control);
}

.queue-photo__text {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.queue-photo__code {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

.queue-photo__time {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}
</style>
